import { GameDisplay } from "../../http/protocol";

/**
 * The path segment the player frontend builds from a page, tab or category name. It must stay identical to
 * the player frontend's own rule, or the search result preview shows a path players never see.
 */
export function toSeoFriendlySlug(name?: string): string {
  if (!name) {
    return "";
  }
  return name.toLocaleLowerCase().split(" ").join("-");
}

function breadcrumb(segments: (string | number | undefined)[]): string {
  return segments
    .filter((segment) => segment !== undefined && segment !== "")
    .map(String)
    .join(" › ");
}

export interface CasinoSeoUrlPartsType {
  pageId?: number;
  pageName?: string;
  defaultPage?: boolean;
  tabId?: number;
  tabName?: string;
}

/**
 * The player path of a casino page or tab as a search result breadcrumb, following the player
 * frontend's goToLobby: /casino/page/{id}/{slug}/{tabId}/{tabSlug}, except that the default page
 * lives at /casino and its tabs at /casino/{tabId}/{tabSlug}. The operator's domain is not known in
 * the back office, so it is left out; an id not assigned yet is left out rather than guessed.
 */
export function casinoSeoDisplayUrl({ pageId, pageName, defaultPage, tabId, tabName }: CasinoSeoUrlPartsType): string {
  const segments = defaultPage ? ["casino"] : ["casino", "page", pageId, toSeoFriendlySlug(pageName)];
  if (tabName !== undefined) {
    segments.push(tabId, toSeoFriendlySlug(tabName));
  }
  return breadcrumb(segments);
}

export interface CasinoCategorySeoUrlPartsType {
  id?: number;
  name?: string;
}

/** The player path of a casino category as a search result breadcrumb, following the player frontend's /casino/category/{id}/{slug}. */
export function casinoCategorySeoDisplayUrl({ id, name }: CasinoCategorySeoUrlPartsType): string {
  return breadcrumb(["casino", "category", id, toSeoFriendlySlug(name)]);
}

const ACCENTED = "àáäâãåăæçèéëêǵḧìíïîḿńǹñòóöôœṕŕßśșțùúüûǘẃẍÿź·/_,:;";
const UNACCENTED = "aaaaaaaaceeeeghiiiimnnnoooooprssstuuuuuwxyz------";
const ACCENTED_PATTERN = new RegExp(ACCENTED.split("").join("|"), "g");

/**
 * The path segment the player frontend builds from a game name (its prettyUrl), which unlike
 * toSeoFriendlySlug also folds accents and drops punctuation. It must stay identical to the player
 * frontend's rule, or the search result preview shows a path players never see.
 */
export function toGameUrlSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(ACCENTED_PATTERN, (character) => UNACCENTED.charAt(ACCENTED.indexOf(character)))
    .replace(/&/g, "-and-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

export interface CasinoGameSeoUrlPartsType {
  id?: number;
  name?: string;
  display?: GameDisplay;
}

/**
 * The player path of a casino game as a search result breadcrumb, following the player frontend's
 * toLaunchUrl: /casino/play/{id}/{slug}/auto, except that an embedded game with a name lives at
 * /originals/{slug}.
 */
export function casinoGameSeoDisplayUrl({ id, name, display }: CasinoGameSeoUrlPartsType): string {
  if (name && display === GameDisplay.EMBEDDED) {
    return breadcrumb(["originals", toGameUrlSlug(name)]);
  }
  return breadcrumb(["casino", "play", id, toGameUrlSlug(name ?? ""), "auto"]);
}
